import {
  Box,
  Flex,
  FormLabel,
  Grid,
  Spacer,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import AppIconButton from "@/features/line-sheet-sets/app-icon-button";
import { useMemo, useState } from "react";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import AppLineSheetSetStatusLabel from "@/features/ui/app-line-sheet-set-status-label";
import AppOrderSheetStatusLabel from "@/features/ui/app-order-sheet-status-label";
import AppLabel from "@/features/ui/app-label";
import SVG from "react-inlinesvg";
import { LineSheetSetStatus } from "./line-sheet-set.types";
import { GetLineSheetSetResponse } from "./hooks/use-get-line-sheet-set";
import {
  OrderSheetSet,
  OrderSheetStatus,
} from "@/features/order-sheets/order-sheet.type";
import useDownloadUnoFile from "@/hooks/use-download-uno-file";
import HelpTooltip from "@/shared/components/help-tooltip";
import { Trans } from "react-i18next";
import { fileIconsByType } from "@/shared/constants";
import MoqNoticeBanner, {
  getOrderMoq,
} from "@/features/order-sheet-sets/moq-notice-banner";
import { canOrderByUnit } from "@/features/line-sheet-sets/helpers/can-order-by-unit";

interface SheetInfoPanelProps {
  onClose: () => void;
  lineSheetSet?: GetLineSheetSetResponse;
  orderSheetSet?: OrderSheetSet;
  // 주문서 페이지에서만 켠다. 라인시트세트 페이지에는 MOQ 안내를 띄우지 않는다.
  showMoqNotice?: boolean;
}

export default function SheetInfoPanel({
  onClose,
  lineSheetSet,
  orderSheetSet,
  showMoqNotice = false,
}: SheetInfoPanelProps) {
  const { tTitle, tSentence, tLocalDateString, tLocalDateTimeString2 } =
    useI18nHelper();
  const [isBrandExpanded, setIsBrandExpanded] = useState(false);

  const { fire: fireDownloadUnoFile } = useDownloadUnoFile();

  const brandText = useMemo(() => {
    if (
      !lineSheetSet?.brands ||
      lineSheetSet.brands === null ||
      lineSheetSet.brands.length === 0
    ) {
      return "";
    }
    const brandNames = lineSheetSet.brands.map((brand) => brand.name);
    return brandNames.join(", ");
  }, [lineSheetSet?.brands]);

  const orderRulesText = useMemo(() => {
    if (!lineSheetSet?.orderRules || lineSheetSet.orderRules.length === 0) {
      return "-";
    }
    return lineSheetSet.orderRules.join("\n");
  }, [lineSheetSet?.orderRules]);

  const attachmentFiles = useMemo(() => {
    return lineSheetSet?.attachments ?? [];
  }, [lineSheetSet?.attachments]);

  const hasOrderRules = useMemo(() => {
    return lineSheetSet?.orderRules?.some((rule) => rule.trim() !== "");
  }, [lineSheetSet?.orderRules]);

  // 소량 주문 가능 세트(패스픽 오픈 중인 스탁오더)는 제출 하한도 없고 권장주문금액 안내도 띄우지 않는다.
  const moq =
    showMoqNotice && !canOrderByUnit(lineSheetSet)
      ? getOrderMoq(lineSheetSet)
      : null;

  return (
    <Flex flexDirection={"column"} height={"100%"} width={"334px"}>
      <Flex
        padding={"12px 16px"}
        justifyContent={"space-between"}
        alignItems={"center"}
        border={"0.5px solid var(--Gray, #D4D4D1)"}
        background={"var(--White, #FFF)"}
      >
        <Text textStyle={"body-md-bold"}>{tTitle("sheet_info")}</Text>
        <AppIconButton
          ariaLabel={"close sheet info"}
          icon={"x"}
          size={"small"}
          onClick={onClose}
        />
      </Flex>
      <Box
        flexGrow={"1"}
        minHeight={0}
        borderRight={"0.5px solid var(--Gray, #D4D4D1)"}
        borderLeft={"0.5px solid var(--Gray, #D4D4D1)"}
        background={"var(--White, #FFF)"}
        marginBottom={"16px"}
        overflowY={"auto"}
      >
        <Box padding={"16px"}>
          <Flex flexDirection={"column"} gap={"16px"}>
            <Grid
              gridTemplateColumns={"68px 1fr"}
              gridAutoRows={"24px"}
              alignItems={"center"}
              columnGap={"6px"}
              rowGap={"8px"}
            >
              {lineSheetSet?.issuedByBoutique && (
                <>
                  <FormLabel
                    fontSize={"13px"}
                    padding={"0"}
                    margin={"0"}
                    fontWeight={"500"}
                    whiteSpace={"nowrap"}
                    color={"#6F6F6C"}
                  >
                    {tTitle("supplier")}
                  </FormLabel>
                  <Text textStyle={"body-md-medium"} color={"#363636"}>
                    {lineSheetSet.issuedByBoutique.name}
                  </Text>
                </>
              )}
              {lineSheetSet?.publishedAt && (
                <>
                  <FormLabel
                    fontSize={"13px"}
                    padding={"0"}
                    margin={"0"}
                    fontWeight={"500"}
                    whiteSpace={"nowrap"}
                    color={"#6F6F6C"}
                  >
                    {tTitle("issued_on")}
                  </FormLabel>
                  <Text textStyle={"body-md-medium"} color={"#363636"}>
                    {tLocalDateString(lineSheetSet.publishedAt)}
                  </Text>
                </>
              )}
              {lineSheetSet?.validUntil && (
                <>
                  <FormLabel
                    fontSize={"13px"}
                    padding={"0"}
                    margin={"0"}
                    fontWeight={"500"}
                    whiteSpace={"nowrap"}
                    color={"#6F6F6C"}
                  >
                    {tTitle("order_valid_until")}
                  </FormLabel>
                  <Text textStyle={"body-md-medium"} color={"#363636"}>
                    {tLocalDateTimeString2(lineSheetSet.validUntil)}
                  </Text>
                </>
              )}
            </Grid>
            {attachmentFiles.length > 0 && (
              <Flex
                flexDirection={"column"}
                padding={"12px"}
                gap={"6px"}
                background={"#F7F9FA"}
                borderRadius={"6px"}
              >
                {/* 참고 파일 */}
                <FormLabel
                  fontSize={"13px"}
                  padding={"0"}
                  margin={"0"}
                  fontWeight={"700"}
                  whiteSpace={"nowrap"}
                >
                  {tTitle("reference_file")}
                </FormLabel>
                <Flex
                  alignItems={"center"}
                  gap={"8px"}
                  padding={"8px"}
                  borderRadius={"4px"}
                  border={"0.5px solid #D4D4D1"}
                  background={"#FCFCFE"}
                  flexDirection={"column"}
                >
                  {attachmentFiles.map((attachmentFile) => (
                    <Flex
                      alignItems={"center"}
                      gap={"8px"}
                      padding={"8px"}
                      borderRadius={"4px"}
                      border={"0.5px solid #D4D4D1"}
                      background={"#FCFCFE"}
                      width={"100%"}
                      key={attachmentFile.id}
                    >
                      <Box flexShrink={0}>
                        <SVG
                          src={
                            fileIconsByType[
                              attachmentFile.name
                                .split(".")
                                .at(1)
                                ?.toLowerCase() ?? "default"
                            ] ?? fileIconsByType.default
                          }
                          width={"20px"}
                          height={"20px"}
                        />
                      </Box>
                      <Text
                        textStyle={"body-sm-bold"}
                        color={"#444444"}
                        flexGrow={1}
                        overflow={"hidden"}
                        textOverflow={"ellipsis"}
                        whiteSpace={"nowrap"}
                      >
                        {attachmentFile.name}
                      </Text>
                      <Box
                        flexShrink={0}
                        cursor={"pointer"}
                        onClick={() => {
                          fireDownloadUnoFile(
                            {
                              unoFileId: attachmentFile.id,
                            },
                            {}
                          );
                        }}
                      >
                        <SVG
                          src={"/icons-v1_1/download.svg"}
                          width={"16px"}
                          height={"16px"}
                        />
                      </Box>
                    </Flex>
                  ))}
                </Flex>
              </Flex>
            )}

            {/*<Box*/}
            {/*  height={"0.5px"}*/}
            {/*  width={"100%"}*/}
            {/*  backgroundColor={"#8F8F8C"}*/}
            {/*  marginY={"8px"}*/}
            {/*/>*/}

            {/*/!* 오더 룰 *!/*/}
            {/*<FormLabel*/}
            {/*  fontSize={"12px"}*/}
            {/*  padding={"0"}*/}
            {/*  margin={"0"}*/}
            {/*  fontWeight={"700"}*/}
            {/*  whiteSpace={"nowrap"}*/}
            {/*>*/}
            {/*  {tTitle("order_rule")}*/}
            {/*</FormLabel>*/}

            {moq !== null && (
              <MoqNoticeBanner moq={moq} currency={lineSheetSet?.currency} />
            )}

            <Flex
              flexDirection={"column"}
              padding={"12px"}
              gap={"6px"}
              background={"#FFF7CC"}
              borderRadius={"6px"}
            >
              <Flex gap={"4px"} alignItems={"center"}>
                <Text textStyle={"body-sm-bold"}>{tTitle("order_rule")}</Text>
                <HelpTooltip
                  text={
                    <Trans
                      i18nKey={"order_rule_panel_tooltip"}
                      components={{
                        br: <br />,
                      }}
                    />
                  }
                />
                {/*<SVG*/}
                {/*  src={"/icons-v1_1/info-circle.svg"}*/}
                {/*  width={"16px"}*/}
                {/*  height={"16px"}*/}
                {/*/>*/}
              </Flex>
              {/* 오더 룰 파일 */}
              {lineSheetSet?.orderRuleFiles &&
                lineSheetSet.orderRuleFiles.length > 0 && (
                  <Flex flexDirection={"column"} gap={"4px"} marginTop={"8px"}>
                    {lineSheetSet.orderRuleFiles.map((file, index) => {
                      const fileType =
                        file.name
                          .toLowerCase()
                          .split(".")
                          .at(1)
                          ?.toLowerCase() ?? "default";
                      return (
                        <Flex
                          key={index}
                          alignItems={"center"}
                          gap={"8px"}
                          padding={"8px"}
                          borderRadius={"4px"}
                          border={"0.5px solid #D4D4D1"}
                          background={"#FCFCFE"}
                        >
                          <Box flexShrink={0}>
                            <SVG
                              src={
                                fileIconsByType[fileType] ??
                                fileIconsByType.default
                              }
                              width={"20px"}
                              height={"20px"}
                            />
                          </Box>
                          <Text
                            textStyle={"body-sm-bold"}
                            color={"#444444"}
                            flexGrow={1}
                            overflow={"hidden"}
                            textOverflow={"ellipsis"}
                            whiteSpace={"nowrap"}
                          >
                            {file.name}
                          </Text>
                          <Box
                            flexShrink={0}
                            cursor={"pointer"}
                            onClick={() => {
                              fireDownloadUnoFile(
                                {
                                  unoFileId: file.id,
                                },
                                {}
                              );
                            }}
                          >
                            <SVG
                              src={"/icons-v1_1/download.svg"}
                              width={"16px"}
                              height={"16px"}
                            />
                          </Box>
                        </Flex>
                      );
                    })}
                  </Flex>
                )}

              {hasOrderRules ? (
                <Flex flexDirection={"column"} gap={"4px"}>
                  <Text
                    fontSize={"12px"}
                    whiteSpace={"pre-wrap"}
                    lineHeight={1.6}
                  >
                    {orderRulesText}
                  </Text>
                </Flex>
              ) : (
                <Text fontSize={"12px"}>-</Text>
              )}
            </Flex>
          </Flex>
        </Box>
      </Box>
    </Flex>
  );
}
